import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { Colors } from '../../../utils/budget-tool-specific';
import { setDocument, listenToDocument } from '../../../utils/firestore-functions';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import styled from 'styled-components';

const SettingsCalculator = (props) => {
    const [calculatorData, setCalculatorData] = useState(null);
    const budgetDB = GetDB();

    // Default values to use if no data exists in database
    const defaultCalculatorData = {
        style: {
            'company-retreat': 0,
            'incentive-trip': 0,
            'team-building': 0,
            'conference-meeting': 0
        },
        accommodations: {
            hotel: 0,
            'all-inclusive': 0,
            resort: 0
        },
        experienceType: {
            '3-star': 0,
            '4-star': 0,
            '5-star': 0
        },
        roomType: {
            'single-rooms': 0,
            'shared-double-occupancy': 0
        },
        retreatAgenda: {
            'all-work': {
                numberOfActivities: 2,
                numberOfCoffeeBreaks: 3
            },
            'mostly-work': {
                numberOfActivities: 3,
                numberOfCoffeeBreaks: 3
            },
            mix: {
                numberOfActivities: 4,
                numberOfCoffeeBreaks: 2
            },
            'mostly-play': {
                numberOfActivities: 5,
                numberOfCoffeeBreaks: 2
            },
            'all-play': {
                numberOfActivities: 6,
                numberOfCoffeeBreaks: 1
            }
        },
        activityCosts: {
            activityCost: 150,
            coffeeBreakCost: 25
        },
        fAndBCosts: {
            breakfast: 25,
            lunch: 35,
            dinner: 45,
            drinks: 15
        },
        destination: {
            canada: 0,
            usa: 0,
            'mexico-caribbean': 0,
            europe: 0,
            'latin-america': 0,
            'asia-pacific': 0,
            'middle-east-africa': 0
        },
        currency: {
            USD: 1.0,
            EUR: 0.93,
            GBP: 0.79,
            CAD: 1.36
        },
        miscFee: 15,
        emailThreshold: 10000
    };

    useEffect(() => {
        // Listen to the Calculator document in the Settings collection
        const unsubscribe = listenToDocument(budgetDB, "Settings", "Calculator", (data) => {
            if (data) {
                // Migrate old retreat agenda structure if needed
                let migratedData = { ...data };
                
                // Check if retreatAgenda exists and needs migration
                if (data.retreatAgenda) {
                    const needsMigration = Object.values(data.retreatAgenda).some(value => 
                        typeof value === 'number'
                    );
                    
                    if (needsMigration) {
                        // Migrate from old structure (numbers) to new structure (objects)
                        const oldAgendaData = data.retreatAgenda;
                        migratedData.retreatAgenda = {
                            'all-work': {
                                numberOfActivities: 2,
                                numberOfCoffeeBreaks: 3
                            },
                            'mostly-work': {
                                numberOfActivities: 3,
                                numberOfCoffeeBreaks: 3
                            },
                            mix: {
                                numberOfActivities: 4,
                                numberOfCoffeeBreaks: 2
                            },
                            'mostly-play': {
                                numberOfActivities: 5,
                                numberOfCoffeeBreaks: 2
                            },
                            'all-play': {
                                numberOfActivities: 6,
                                numberOfCoffeeBreaks: 1
                            }
                        };
                        
                        // Save the migrated data back to the database
                        setDocument(budgetDB, "Settings", "Calculator", migratedData);
                    }
                }
                
                // Check if destination needs migration from canada-usa to separate canada and usa
                if (data.destination && data.destination['canada-usa'] !== undefined) {
                    const oldCanadaUsaValue = data.destination['canada-usa'];
                    migratedData.destination = {
                        ...data.destination,
                        canada: oldCanadaUsaValue,
                        usa: oldCanadaUsaValue,
                    };
                    delete migratedData.destination['canada-usa'];
                    
                    // Save the migrated data back to the database
                    setDocument(budgetDB, "Settings", "Calculator", migratedData);
                }
                
                // Check if F&B cost needs migration from single value to separate meal costs
                if (data.fAndBCost !== undefined && !data.fAndBCosts) {
                    const oldFAndBCost = data.fAndBCost;
                    migratedData.fAndBCosts = {
                        breakfast: Math.round(oldFAndBCost * 0.3), // 30% of total
                        lunch: Math.round(oldFAndBCost * 0.4),     // 40% of total
                        dinner: Math.round(oldFAndBCost * 0.25),   // 25% of total
                        drinks: Math.round(oldFAndBCost * 0.05)    // 5% of total
                    };
                    delete migratedData.fAndBCost;
                    
                    // Save the migrated data back to the database
                    setDocument(budgetDB, "Settings", "Calculator", migratedData);
                }
                
                // Merge with default data to ensure all fields exist
                const mergedData = {
                    ...defaultCalculatorData,
                    ...migratedData
                };
                setCalculatorData(mergedData);
            } else {
                // If no data exists, use default values
                setCalculatorData(defaultCalculatorData);
            }
        });

        return unsubscribe;
    }, [budgetDB]);

    const updateNestedValue = (path, value) => {
        const newData = { ...calculatorData };
        
        // Special handling for miscFee since it's a direct value, not a nested object
        if (path === 'miscFee.misc-fee') {
            newData.miscFee = parseFloat(value) || 0;
        } else {
            const keys = path.split('.');
            let current = newData;
            
            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }
            
            current[keys[keys.length - 1]] = parseFloat(value) || 0;
        }
        
        setCalculatorData(newData);
        
        // Update the database
        setDocument(budgetDB, "Settings", "Calculator", newData);
    };

    const updateRetreatAgendaValue = (agendaType, field, value) => {
        const newData = { ...calculatorData };
        if (!newData.retreatAgenda) {
            newData.retreatAgenda = {};
        }
        if (!newData.retreatAgenda[agendaType]) {
            newData.retreatAgenda[agendaType] = {};
        }
        newData.retreatAgenda[agendaType][field] = parseInt(value, 10) || 0;
        setCalculatorData(newData);
        
        // Update the database
        setDocument(budgetDB, "Settings", "Calculator", newData);
    };

    const updateActivityCostValue = (field, value) => {
        const newData = { ...calculatorData };
        if (!newData.activityCosts) {
            newData.activityCosts = {};
        }
        newData.activityCosts[field] = parseFloat(value) || 0;
        setCalculatorData(newData);
        
        // Update the database
        setDocument(budgetDB, "Settings", "Calculator", newData);
    };

    const updateFAndBCostValue = (mealType, value) => {
        const newData = { ...calculatorData };
        if (!newData.fAndBCosts) {
            newData.fAndBCosts = {};
        }
        newData.fAndBCosts[mealType] = parseFloat(value) || 0;
        setCalculatorData(newData);
        
        // Update the database
        setDocument(budgetDB, "Settings", "Calculator", newData);
    };

    const renderCostSection = (title, costs, path) => {
        const isDecimalField = title === 'Currency' || title === 'Misc. Fee (%)';
        const stepValue = isDecimalField ? "0.01" : "1";
        
        // Create display labels for each section
        const getDisplayLabel = (key, section) => {
            const labelMappings = {
                style: {
                    'company-retreat': 'Company Retreat',
                    'incentive-trip': 'Incentive Trip',
                    'team-building': 'Team-Building',
                    'conference-meeting': 'Conference/Meeting'
                },
                accommodations: {
                    hotel: 'Hotel',
                    'all-inclusive': 'All-Inclusive',
                    resort: 'Resort'
                },
                experienceType: {
                    '3-star': '3 Star',
                    '4-star': '4 Star',
                    '5-star': '5 Star'
                },
                roomType: {
                    'single-rooms': 'Single Rooms',
                    'shared-double-occupancy': 'Shared (Double Occupancy)'
                },
                destination: {
                    canada: 'Canada',
                    usa: 'USA',
                    'mexico-caribbean': 'Mexico & Caribbean',
                    europe: 'Europe',
                    'latin-america': 'Latin America',
                    'asia-pacific': 'Asia-Pacific',
                    'middle-east-africa': 'Middle East & Africa'
                },
                currency: {
                    USD: 'USD',
                    EUR: 'EUR',
                    GBP: 'GBP',
                    CAD: 'CAD'
                },
                miscFee: {
                    'misc-fee': 'Percentage'
                }
            };
            
            return labelMappings[section]?.[key] || key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        };
        
        // Sort the costs by value in ascending order
        const sortedCosts = Object.entries(costs).sort((a, b) => a[1] - b[1]);
        
        return (
            <SectionContainer>
                <SectionTitle>{title}</SectionTitle>
                <Row>
                    {sortedCosts.map(([key, value]) => (
                        <Col key={key} md={6} lg={4}>
                            <CostItem>
                                <CostLabel>{getDisplayLabel(key, path)}</CostLabel>
                                <StyledInput
                                    type="number"
                                    value={value}
                                    onChange={(e) => updateNestedValue(`${path}.${key}`, e.target.value)}
                                    step={stepValue}
                                    min="0"
                                />
                            </CostItem>
                        </Col>
                    ))}
                </Row>
            </SectionContainer>
        );
    };

    const renderRetreatAgendaSection = () => {
        const agendaLabels = {
            'all-work': 'All Work',
            'mostly-work': 'Mostly Work',
            mix: 'Mix',
            'mostly-play': 'Mostly Play',
            'all-play': 'All Play'
        };

        return (
            <SectionContainer>
                <SectionTitle>Retreat Agenda</SectionTitle>
                <p>Set the number of activities and coffee breaks for each retreat type:</p>
                <Row>
                    {Object.entries(calculatorData.retreatAgenda).map(([agendaType, agendaData]) => (
                        <Col key={agendaType} md={6} lg={4}>
                            <AgendaItem>
                                <AgendaLabel>{agendaLabels[agendaType]}</AgendaLabel>
                                <InputGroup>
                                    <InputLabel>Number of Activities:</InputLabel>
                                    <StyledInput
                                        type="number"
                                        min="0"
                                        value={agendaData.numberOfActivities || 0}
                                        onChange={(e) => updateRetreatAgendaValue(agendaType, 'numberOfActivities', e.target.value)}
                                    />
                                </InputGroup>
                                <InputGroup>
                                    <InputLabel>Number of Coffee Breaks:</InputLabel>
                                    <StyledInput
                                        type="number"
                                        min="0"
                                        value={agendaData.numberOfCoffeeBreaks || 0}
                                        onChange={(e) => updateRetreatAgendaValue(agendaType, 'numberOfCoffeeBreaks', e.target.value)}
                                    />
                                </InputGroup>
                            </AgendaItem>
                        </Col>
                    ))}
                </Row>
            </SectionContainer>
        );
    };

    const renderActivityCostsSection = () => {
        return (
            <SectionContainer>
                <SectionTitle>Activity & Coffee Break Costs</SectionTitle>
                <p>Define the costs for activities and coffee breaks:</p>
                <Row>
                    <Col md={6}>
                        <CostItem>
                            <CostLabel>Cost per Activity (USD)</CostLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                step="0.01"
                                value={calculatorData.activityCosts?.activityCost || 0}
                                onChange={(e) => updateActivityCostValue('activityCost', e.target.value)}
                            />
                        </CostItem>
                    </Col>
                    <Col md={6}>
                        <CostItem>
                            <CostLabel>Cost per Coffee Break (USD)</CostLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                step="0.01"
                                value={calculatorData.activityCosts?.coffeeBreakCost || 0}
                                onChange={(e) => updateActivityCostValue('coffeeBreakCost', e.target.value)}
                            />
                        </CostItem>
                    </Col>
                </Row>
            </SectionContainer>
        );
    };

    const renderFAndBCostSection = () => {
        return (
            <SectionContainer>
                <SectionTitle>Food & Beverage Costs</SectionTitle>
                <p>Set the cost per person for each meal type. Note: Breakfast, Dinner, and Drinks are calculated for (num nights - 1), while Lunch is calculated for (num nights):</p>
                <Row>
                    <Col md={6} lg={3}>
                        <CostItem>
                            <CostLabel>Breakfast Cost per Person (USD)</CostLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                step="0.01"
                                value={calculatorData.fAndBCosts?.breakfast || 0}
                                onChange={(e) => updateFAndBCostValue('breakfast', e.target.value)}
                            />
                        </CostItem>
                    </Col>
                    <Col md={6} lg={3}>
                        <CostItem>
                            <CostLabel>Lunch Cost per Person (USD)</CostLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                step="0.01"
                                value={calculatorData.fAndBCosts?.lunch || 0}
                                onChange={(e) => updateFAndBCostValue('lunch', e.target.value)}
                            />
                        </CostItem>
                    </Col>
                    <Col md={6} lg={3}>
                        <CostItem>
                            <CostLabel>Dinner Cost per Person (USD)</CostLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                step="0.01"
                                value={calculatorData.fAndBCosts?.dinner || 0}
                                onChange={(e) => updateFAndBCostValue('dinner', e.target.value)}
                            />
                        </CostItem>
                    </Col>
                    <Col md={6} lg={3}>
                        <CostItem>
                            <CostLabel>Drinks Cost per Person (USD)</CostLabel>
                            <StyledInput
                                type="number"
                                min="0"
                                step="0.01"
                                value={calculatorData.fAndBCosts?.drinks || 0}
                                onChange={(e) => updateFAndBCostValue('drinks', e.target.value)}
                            />
                        </CostItem>
                    </Col>
                </Row>
            </SectionContainer>
        );
    };

    // Special handling for email threshold since it's a direct value
    const renderEmailThreshold = () => {
        return (
            <SectionContainer>
                <SectionTitle>Email Threshold</SectionTitle>
                <Row>
                    <Col md={6} lg={4}>
                        <CostItem>
                            <CostLabel>Threshold Amount</CostLabel>
                            <StyledInput
                                type="number"
                                value={calculatorData?.emailThreshold || 0}
                                onChange={(e) => {
                                    const newData = { ...calculatorData };
                                    newData.emailThreshold = parseFloat(e.target.value) || 0;
                                    setCalculatorData(newData);
                                    setDocument(budgetDB, "Settings", "Calculator", newData);
                                }}
                                step="1"
                                min="0"
                            />
                        </CostItem>
                    </Col>
                </Row>
            </SectionContainer>
        );
    };

    if (!calculatorData) {
        return <div>Loading calculator settings...</div>;
    }

    return ( 
        <React.Fragment>
            <SettingsContainer>
                <NotesSection>
                    <NotesTitle>How Cost is Calculated</NotesTitle>
                    <NotesText>
                        Our cost is broken down into 5 sections:
                    </NotesText>
                    <NotesList>
                        <NotesListItem>
                            <strong>Accommodation:</strong> (num of attendees × num nights × accommodations × experience type × room type × destination × currency × style)
                        </NotesListItem>
                        <NotesListItem>
                            <strong>Flights + Transportation:</strong> (this is user entered in the calculator)
                        </NotesListItem>
                        <NotesListItem>
                            <strong>Food & Beverage:</strong> Sum of 4 calculations:
                            <ul style={{marginTop: '5px', marginBottom: '5px'}}>
                                <li><strong>Breakfast:</strong> (num of attendees × (num nights - 1) × breakfast cost per person × experience type × destination × currency × style)</li>
                                <li><strong>Lunch:</strong> (num of attendees × num nights × lunch cost per person × experience type × destination × currency × style)</li>
                                <li><strong>Dinner:</strong> (num of attendees × (num nights - 1) × dinner cost per person × experience type × destination × currency × style)</li>
                                <li><strong>Drinks:</strong> (num of attendees × (num nights - 1) × drinks cost per person × experience type × destination × currency × style)</li>
                            </ul>
                        </NotesListItem>
                        <NotesListItem>
                            <strong>Activities & Meetings:</strong> (num of activities × activity cost + num of coffee breaks × coffee break cost) × destination × currency × style
                        </NotesListItem>
                        <NotesListItem>
                            <strong>Misc Expenses (All anticipated Taxes, Gratuities, and Fees):</strong> This is costs 1, 3 and 4 added together, and then a percentage of that
                        </NotesListItem>
                    </NotesList>
                </NotesSection>
                
                {renderCostSection('Style', calculatorData.style, 'style')}
                {renderCostSection('Accommodations', calculatorData.accommodations, 'accommodations')}
                {renderCostSection('Experience Type', calculatorData.experienceType, 'experienceType')}
                {renderCostSection('Room Type', calculatorData.roomType, 'roomType')}
                {renderRetreatAgendaSection()}
                {renderActivityCostsSection()}
                {renderFAndBCostSection()}
                {renderCostSection('Destination', calculatorData.destination, 'destination')}
                {renderCostSection('Currency', calculatorData.currency, 'currency')}
                {renderCostSection('Misc. Fee (%)', { 'misc-fee': calculatorData.miscFee }, 'miscFee')}
                {renderEmailThreshold()}
            </SettingsContainer>
        </React.Fragment>
    );
};
 
export default SettingsCalculator;

const SettingsContainer = styled.div`
    padding: 20px 0;
`;

const SectionContainer = styled.div`
    margin-bottom: 30px;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h5`
    color: ${Colors.primaryDark};
    margin-bottom: 20px;
    font-weight: 600;
    border-bottom: 2px solid ${Colors.primaryAccent};
    padding-bottom: 8px;
`;

const CostItem = styled.div`
    margin-bottom: 15px;
    padding: 10px;
    background-color: #f1f3f4;
    border-radius: 6px;
    border: 1px solid #dadce0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const AgendaItem = styled.div`
    margin-bottom: 15px;
    padding: 15px;
    background-color: #f1f3f4;
    border-radius: 6px;
    border: 1px solid #dadce0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CostLabel = styled.label`
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    color: ${Colors.primaryDark};
    font-size: 14px;
`;

const AgendaLabel = styled.label`
    display: block;
    margin-bottom: 10px;
    font-weight: 600;
    color: ${Colors.primaryDark};
    font-size: 14px;
    border-bottom: 1px solid #dadce0;
    padding-bottom: 5px;
`;

const InputGroup = styled.div`
    margin-bottom: 10px;
`;

const InputLabel = styled.label`
    display: block;
    margin-bottom: 3px;
    font-weight: 500;
    color: ${Colors.primaryDark};
    font-size: 12px;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${Colors.primaryAccent};
        box-shadow: 0 0 0 2px rgba(253, 181, 6, 0.25);
    }
`;

const NotesSection = styled.div`
    margin-bottom: 30px;
    padding: 20px;
    background-color: #e8f4fd;
    border-radius: 8px;
    border: 1px solid #b3d9f2;
`;

const NotesTitle = styled.h4`
    color: ${Colors.primaryDark};
    margin-bottom: 15px;
    font-weight: 600;
    border-bottom: 2px solid ${Colors.primaryAccent};
    padding-bottom: 8px;
`;

const NotesText = styled.p`
    color: ${Colors.primaryDark};
    font-size: 16px;
    margin-bottom: 15px;
    font-weight: 500;
`;

const NotesList = styled.ol`
    color: ${Colors.primaryDark};
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
    padding-left: 20px;
`;

const NotesListItem = styled.li`
    margin-bottom: 8px;
    
    strong {
        color: ${Colors.primaryDark};
        font-weight: 600;
    }
`;